import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-182cd4ae"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  xs12: "",
  sm10: "",
  "offset-sm1": ""
}

import { ref } from 'vue';
import MediaStreamRecorder from 'msr';
import urlJoin from 'url-join';
import * as t from 'io-ts';
import screenfull from 'screenfull';
import {mdiEye, mdiEyeOff, mdiMonitorShare, mdiMonitor, mdiFullscreen} from "@mdi/js";


export default /*#__PURE__*/_defineComponent({
  __name: 'PipingScreenShare',
  setup(__props) {

function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      resolve(fileReader.result as ArrayBuffer);
    };
    fileReader.onerror = () => {
      reject(fileReader.error);
    };
    fileReader.readAsArrayBuffer(blob);
  });
}

function createServerUrl(baseServerUrl: string, screenId: string, chunkNum: number) {
  // TODO: Use digest-hash not to give information for Piping Server
  return urlJoin(baseServerUrl, `screen-share-web/${screenId}/${chunkNum}`);
}

async function passphraseToKey(passphrase: string): Promise<Uint8Array> {
  // Convert passphrase string to Uint8Array
  const passphraseU8Array: Uint8Array = new TextEncoder().encode(passphrase);
  // Generate key from passphrase by SHA-2156
  const key = new Uint8Array(await crypto.subtle.digest('SHA-256', passphraseU8Array));
  return key;
}

// AES-GCM (Initial Vector (IV) is attached on the head)
const IvAesGcm = {
  ivLen: 12,

  async createCryptoKey(passphrase: string): Promise<CryptoKey> {
    const passphraseDigest = await passphraseToKey(passphrase);
    return crypto.subtle.importKey(
      'raw',
      passphraseDigest,
      {name: 'AES-GCM', length: 128},
      false,
      ['encrypt', 'decrypt'],
    );
  },

  // NOTE: Return-type of encrypt and decrypt is not symmetric.
  async encryptAsBlob(raw: ArrayBuffer, passphrase: string): Promise<Blob> {
    // Create IV
    const iv = window.crypto.getRandomValues(new Uint8Array(this.ivLen));
    // Create key from passphrase
    const cryptoKey = await this.createCryptoKey(passphrase);
    // Encrypt
    const encrypted = await crypto.subtle.encrypt(
      {name: 'AES-GCM', iv},
      cryptoKey,
      raw,
    );
    return new Blob([iv, encrypted]);
  },

  // NOTE: Return-type of encrypt and decrypt is not symmetric.
  async decryptAsArrayBuffer(encryptedWithIv: ArrayBuffer, passphrase: string): Promise<ArrayBuffer> {
    // Extract IV and encrypted parts
    const iv = encryptedWithIv.slice(0, this.ivLen);
    const encrypted = encryptedWithIv.slice(this.ivLen);
    // Create key from passphrase
    const cryptoKey = await this.createCryptoKey(passphrase);
    return crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      encrypted,
    );
  },
};

function encodeTimestamp(timestamp: bigint): ArrayBuffer {
  const view = new DataView(new ArrayBuffer(8));
  view.setBigUint64(0, timestamp, false);
  return view.buffer;
}

function decodeTimestamp(buf: ArrayBuffer): bigint {
  return new DataView(buf).getBigUint64(0, false);
}

function parseHashAsQuery(): URLSearchParams {
  const url = new URL(`a://a${location.hash.substring(1)}`);
  return url.searchParams;
}

const video0Ref = ref<HTMLVideoElement>();
const video1Ref = ref<HTMLVideoElement>();
const videoContainerRef = ref<HTMLDivElement>();

const shareOrView = ref<'share' | 'view'>((() => {
  const type = parseHashAsQuery().get('type');
  if (type === null) {
    return 'share' as const;
  }
  const typeEither = t.union([t.literal('share'), t.literal('view')]).decode(type);
  if (typeEither._tag === 'Left') {
    return 'share' as const;
  }
  return typeEither.right;
})());

const serverUrl = ref<string>(parseHashAsQuery().get('server') ?? 'https://ppng.io');
const screenId = ref<string>(parseHashAsQuery().get('screen_id') ?? '');
const passphrase = ref<string>('');
const showPassphrase = ref<boolean>(false);
const enableActionButton = ref<boolean>(true);
const showFullscreenButton = ref<boolean>(false);

async function shareScreen() {
  if (!('getDisplayMedia' in navigator.mediaDevices)) {
    console.error('getDisplayMedia is required');
    return;
  }

  // Disable the button
  enableActionButton.value = false;

  const timestampToAbortController: Map<bigint, AbortController> = new Map();

  const stream = await (navigator.mediaDevices as any).getDisplayMedia({video: true});
  const mediaRecorder = new MediaStreamRecorder(stream);
  mediaRecorder.mimeType = 'video/mp4';

  let chunkNum = 0;
  mediaRecorder.ondataavailable = async (blob: Blob) => {
    const timestamp: bigint = BigInt(Date.now());

    // Encrypt
    const encryptedBlob: Blob = await IvAesGcm.encryptAsBlob(
      await blobToArrayBuffer(new Blob([encodeTimestamp(timestamp), blob])),
      passphrase.value,
    );

    const existingAbortController: AbortController | undefined = timestampToAbortController.get(timestamp);
    if (existingAbortController !== undefined) {
      timestampToAbortController.delete(timestamp);
      existingAbortController.abort();
    }

    const abortController = new AbortController();
    // Send a blob
    fetch(createServerUrl(serverUrl.value, screenId.value, chunkNum), {
      method: 'POST',
      body: encryptedBlob,
      signal: abortController.signal,
    });
    timestampToAbortController.set(timestamp, abortController);
    chunkNum = (chunkNum + 1) % 2;
  };

  mediaRecorder.start(500);
}

async function viewScreen() {
  // Disable the button
  enableActionButton.value = false;

  // Queue of blob URL
  const blobUrlQueue: Array<{ blobUrl: string, timestamp: bigint }> = [];
  let active: HTMLVideoElement = video0Ref.value!;
  let hidden: HTMLVideoElement = video1Ref.value!;

  // For waiting the buffer filled
  let waitDoubleBufferResolve: (() => void) | null = null;
  // Played seq number
  let prevPlayedTimestamp: bigint | undefined;

  // Double-buffered
  async function doubleBuffer() {
    await new Promise<void>((resolve) => {
      if (blobUrlQueue.length === 0) {
        waitDoubleBufferResolve = resolve;
      } else {
        resolve();
      }
    });
    console.log('double buffer called');
    // Revoke active blob URL because its play has ended
    URL.revokeObjectURL(active.src);
    // Swap video elements
    [active, hidden] = [hidden, active];
    active.play();
    // NOTE: It is never undefined logically because the queue is not empty
    const {blobUrl, timestamp} = blobUrlQueue.shift()!;
    prevPlayedTimestamp = timestamp;
    hidden.src = blobUrl;
    active.style.display = '';
    hidden.style.display = 'none';
  }

  // Subscribe ended
  active.onended = doubleBuffer;
  hidden.onended = doubleBuffer;

  let firstPlayDone = false;

  const load = async (cycleNum: number): Promise<{res: Response, cycleNum: number}> => {
    try {
      const res = await fetch(createServerUrl(serverUrl.value, screenId.value, cycleNum));
      if (res.status !== 200) {
        throw new Error(`status is not 200, ${res.status}`);
      }
      return {
        res,
        cycleNum,
      };
    } catch (e) {
      setTimeout(() => {
        cyclePromises[cycleNum] = load(cycleNum);
      }, 1000);
      throw e;
    }
  };

  const cyclePromises: Array<Promise<{res: Response, cycleNum: number}>> = [
    load(0),
    load(1),
  ];

  while (true) {
    try {
      // Get a chunk
      const {res, cycleNum} = await Promise.any(cyclePromises);
      // Get body
      const encryptedBuffer = await res.arrayBuffer();
      // Request
      cyclePromises[cycleNum] = load(cycleNum);
      // Decrypt
      const decrypted: ArrayBuffer = await IvAesGcm.decryptAsArrayBuffer(
          encryptedBuffer,
          passphrase.value,
      );

      // Get seq number
      const timestamp = decodeTimestamp(decrypted.slice(0, 8));
      console.log("timestamp=", timestamp, new Date(Number(timestamp)));

      // Skip if the screen is older than played one
      if (prevPlayedTimestamp !== undefined && timestamp < prevPlayedTimestamp) {
        console.log("skip", "prevPlayedTimestamp=", prevPlayedTimestamp, "timestamp=", timestamp);
        continue;
      }

      // Get a blob
      const blob: Blob = new Blob([decrypted.slice(8)], {type: 'video/mp4'});

      if (blob.size === 0) {
        console.log('blob is empty');
        break;
      }

      // Push the blob URL
      const blobUrl = URL.createObjectURL(blob);
      blobUrlQueue.push({
        blobUrl,
        timestamp,
      });

      if (!firstPlayDone && blobUrlQueue.length >= 2) {
        // NOTE: There are never undefined logically because the length queue is over 1
        // FIXME: reorder blob by seq number
        active.src = blobUrlQueue.shift()!.blobUrl;
        hidden.src = blobUrlQueue.shift()!.blobUrl;
        active.play();
        firstPlayDone = true;
        // Enable show fullscreen button
        showFullscreenButton.value = true;
      } else {
        // NOTE: You can chane the threshold >= n
        if (blobUrlQueue.length >= 1 && waitDoubleBufferResolve !== null) {
          // Resolve
          waitDoubleBufferResolve!();
          // Release
          waitDoubleBufferResolve = null;
        }
      }
    } catch (e) {
      console.error(e);
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }
}

function fullScreen() {
  if (screenfull.isEnabled) {
    screenfull.request(videoContainerRef.value!);
  } else {
    console.warn("full screen is not enabled");
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_btn_toggle = _resolveComponent("v-btn-toggle")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_row = _resolveComponent("v-row")!

  return (_openBlock(), _createBlock(_component_v_container, { "text-xs-center": "" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_row, { wrap: "" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_container, {
            xs12: "",
            style: {"margin-bottom":"1em"}
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn_toggle, {
                modelValue: shareOrView.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((shareOrView).value = $event)),
                mandatory: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    variant: "text",
                    value: "share"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(" Share "),
                      _createVNode(_component_v_icon, {
                        right: "",
                        dark: ""
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(mdiMonitorShare)), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    variant: "text",
                    value: "view"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(" View "),
                      _createVNode(_component_v_icon, {
                        right: "",
                        dark: ""
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(mdiMonitor)), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          }),
          (shareOrView.value === 'view')
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", {
                  ref_key: "videoContainerRef",
                  ref: videoContainerRef
                }, [
                  _createElementVNode("video", {
                    ref_key: "video0Ref",
                    ref: video0Ref,
                    style: {"display":"none"}
                  }, null, 512),
                  _createElementVNode("video", {
                    ref_key: "video1Ref",
                    ref: video1Ref,
                    style: {"display":"none"}
                  }, null, 512)
                ], 512),
                (showFullscreenButton.value)
                  ? (_openBlock(), _createBlock(_component_v_icon, {
                      key: 0,
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (fullScreen())),
                      style: {"font-size":"2em"}
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(mdiFullscreen)), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          _createVNode(_component_v_container, {
            xs12: "",
            sm8: "",
            "offset-sm2": "",
            "offset-md3": "",
            md6: ""
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, { style: {"padding":"1em"} }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    type: "text",
                    modelValue: serverUrl.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((serverUrl).value = $event)),
                    label: "Server URL"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_v_text_field, {
                    type: "text",
                    modelValue: screenId.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((screenId).value = $event)),
                    label: "Screen ID",
                    placeholder: "Input screen ID"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_v_text_field, {
                    label: "Passphrase (optional)",
                    modelValue: passphrase.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((passphrase).value = $event)),
                    placeholder: "Input passphrase",
                    type: showPassphrase.value ? 'text' : 'password',
                    "append-icon": showPassphrase.value ? _unref(mdiEye) : _unref(mdiEyeOff),
                    "onClick:append": _cache[5] || (_cache[5] = ($event: any) => (showPassphrase.value = !showPassphrase.value))
                  }, null, 8, ["modelValue", "type", "append-icon"]),
                  (shareOrView.value === 'share')
                    ? (_openBlock(), _createBlock(_component_v_btn, {
                        key: 0,
                        color: "primary",
                        onClick: _cache[6] || (_cache[6] = ($event: any) => (shareScreen())),
                        disabled: !enableActionButton.value,
                        style: {"width":"100%"}
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(" Share "),
                          _createVNode(_component_v_icon, {
                            right: "",
                            dark: ""
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_unref(mdiMonitorShare)), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["disabled"]))
                    : _createCommentVNode("", true),
                  (shareOrView.value === 'view')
                    ? (_openBlock(), _createBlock(_component_v_btn, {
                        key: 1,
                        color: "secondary",
                        onClick: _cache[7] || (_cache[7] = ($event: any) => (viewScreen())),
                        disabled: !enableActionButton.value,
                        style: {"width":"100%"}
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(" View "),
                          _createVNode(_component_v_icon, {
                            right: "",
                            dark: ""
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_unref(mdiMonitor)), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["disabled"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})